import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import TextAlignment from '../../enums/text-aligment.enum';
import { CycleHighestMetrics } from './types/screen-cycle-table';

export type OptionEntry = {
  NAME: string;
  KEY: keyof CycleHighestMetrics | 'cycle_device_actions' | 'actions' | 'completed_date';
  BLOCK_PARAMETERS: string;
  ALIGNMENT: TextAlignment;
};

export enum OptionKey {
  NAME = 'NAME',
  COMPLETED_DATE = 'COMPLETED_DATE',
  CYCLE_DEVICE_ACTIONS = 'CYCLE_DEVICE_ACTIONS',
  HIGHEST_TEMP = 'HIGHEST_TEMP',
  LOWEST_TEMP = 'LOWEST_TEMP',
  HIGHEST_SP = 'HIGHEST_SP',
  HIGHEST_P = 'HIGHEST_P',
  LOWEST_P = 'LOWEST_P',
  MIN_DELTA_T = 'MIN_DELTA_T',
  MAX_DELTA_T = 'MAX_DELTA_T',
  HIGHEST_STATE = 'HIGHEST_STATE',
  ACTIONS = 'ACTIONS',
  STEP = 'STEP',
  TIME_LEFT = 'TIME_LEFT'
}

export type Option = Record<OptionKey, OptionEntry>;

// The columns a user is allowed to hide via the column-visibility picker -
// NAME, CYCLE_DEVICE_ACTIONS and ACTIONS stay off this list on purpose,
// they're structural (identity/controls), not data a user would want to
// toggle off.
export const HIDEABLE_OPTION_KEYS: OptionKey[] = [
  OptionKey.COMPLETED_DATE,
  OptionKey.HIGHEST_TEMP,
  OptionKey.LOWEST_TEMP,
  OptionKey.HIGHEST_SP,
  OptionKey.HIGHEST_P,
  OptionKey.LOWEST_P,
  OptionKey.MIN_DELTA_T,
  OptionKey.MAX_DELTA_T,
  OptionKey.HIGHEST_STATE,
  OptionKey.STEP,
  OptionKey.TIME_LEFT,
];

// Plain labels for the column-visibility picker, which lives once at the top
// of a page rather than per-table - so it can't use the temperature-unit-
// suffixed labels from getOptions() below (a page can show cycles from
// contracts on different measurement units at once).
export const HIDEABLE_OPTION_LABELS: Record<OptionKey, string> = {
  [OptionKey.NAME]: 'Name',
  [OptionKey.COMPLETED_DATE]: 'Completed Date',
  [OptionKey.CYCLE_DEVICE_ACTIONS]: 'Cycle device actions',
  [OptionKey.HIGHEST_TEMP]: 'Max Temp',
  [OptionKey.LOWEST_TEMP]: 'Min Temp',
  [OptionKey.HIGHEST_SP]: 'SP',
  [OptionKey.HIGHEST_P]: 'Max Present',
  [OptionKey.LOWEST_P]: 'Min Present',
  [OptionKey.MIN_DELTA_T]: 'Min ΔT',
  [OptionKey.MAX_DELTA_T]: 'Max ΔT',
  [OptionKey.HIGHEST_STATE]: 'State',
  [OptionKey.ACTIONS]: 'Actions',
  [OptionKey.STEP]: 'Step',
  [OptionKey.TIME_LEFT]: 'Time Left',
};

const createOption = (name: string, key: OptionEntry['KEY'], block: string, alignment: TextAlignment): OptionEntry => ({
  NAME: name,
  KEY: key,
  BLOCK_PARAMETERS: block,
  ALIGNMENT: alignment,
});

export const getOptions = (measurement: keyof typeof CycleMeasurementSymbol): Option => {
  const commonOptions: Option = {
    NAME: createOption('Name', 'name', 'min-w-[140px] pl-2 align-start', TextAlignment.Start),
    COMPLETED_DATE: createOption(
      'Completed Date',
      'completed_date',
      'min-w-[140px] pl-2 align-start',
      TextAlignment.Start,
    ),
    CYCLE_DEVICE_ACTIONS: createOption(
      'Cycle device actions',
      'cycle_device_actions',
      'min-w-[180px]',
      TextAlignment.Center,
    ),
    HIGHEST_TEMP: createOption(
      `Max ${CycleMeasurementSymbol[measurement]}`,
      'highest_temp',
      'min-w-[46px]',
      TextAlignment.Center,
    ),
    LOWEST_TEMP: createOption(
      `Min ${CycleMeasurementSymbol[measurement]}`,
      'lowest_temp',
      'min-w-[46px]',
      TextAlignment.Center,
    ),
    HIGHEST_SP: createOption('SP', 'highest_sp', 'min-w-[46px]', TextAlignment.Center),
    HIGHEST_P: createOption('Max P', 'highest_present', 'min-w-[46px]', TextAlignment.Center),
    LOWEST_P: createOption('Min P', 'lowest_present', 'min-w-[46px]', TextAlignment.Center),
    MIN_DELTA_T: createOption(
      `Min ΔT ${CycleMeasurementSymbol[measurement]}`,
      'min_delta_t',
      'min-w-[70px]',
      TextAlignment.Center,
    ),
    MAX_DELTA_T: createOption(
      `Max ΔT ${CycleMeasurementSymbol[measurement]}`,
      'max_delta_t',
      'min-w-[70px]',
      TextAlignment.Center,
    ),
    STEP: createOption('S', 'step', 'min-w-[36px]', TextAlignment.Center),
    TIME_LEFT: createOption('T', 'time_left', 'min-w-[36px]', TextAlignment.Center),
    HIGHEST_STATE: createOption('State', 'highest_state', 'min-w-[46px]', TextAlignment.Center),
    ACTIONS: createOption('', 'actions', 'min-w-[46px]', TextAlignment.Center),
  };

  return commonOptions;
};

export default getOptions;
